/*
 * Your order: the hub's lines (held at a store, priced by the hub) beside Shopify's own cart (posted merch, drops, gift
 * cards), on /cart and as the Order page's side panel. What's held, what it costs and whether it's paid is the hub's to
 * say, so the cart asks it on every visit and after every change; a read that fails says "Can't check", never held, sold
 * out or $0. Checkout goes through the hub (a courier quote first for a delivery), then to Shopify's invoice. Drops and
 * gift cards check out on their own. Ported from phase 5's ms-hub-cart.js, whose judged states carry over.
 */
import { hub, loadCart, saveCart, emptyCart, cartLive, lineKey, settle, settleLast, lastPaidStore, cleanPaidMerch, paidMerchLeft, keepInvoice, safeInvoice, randomKey, storageOk, signInUrl, zoneFor, todayIn, storeChoice, safePath } from "./ms-hub.js";
import { money, timeLabel, dayLabel } from "./ms-format.js";
import { splitWords } from "./ms-tub.js";
import { barNamesWords } from "./ms-box.js";

/** The till's "CHOCOLATE BOX OF 4" as a person reads it: "Box of 4". */
const boxTitle = (name) => { const m = /BOX OF (\d+)/i.exec(String(name || "")); return m ? `Box of ${m[1]}` : name; };

const TIMES_KEY = "ms-hub-pickup-times-v1";

// ── The rules (tested in tests/cart.test.mjs) ─────────────────────────────────────────────────────────────────────

const kindWord = (lines) => {
  const kinds = new Set(lines.map((l) => l.kind));
  if (kinds.size === 1 && kinds.has("tub")) return lines.length > 1 ? "tubs" : "tub";
  if (kinds.size === 1 && kinds.has("cake")) return lines.length > 1 ? "cakes" : "cake";
  return "order";
};

/** What's in a line, under its name: a tub's split biggest first ("2/3 Ab's Weakness, 1/3 Gooey"), its flavours when
 *  even, "Flavours at the counter" for a tub for later or scoops, "Made at the counter" for an extra special, a size. */
export function lineWhat(x) {
  if (x.kind === "chocolate") return barNamesWords(x.barNames);
  const names = x.flavourNames || [];
  if (x.kind === "tub" && !names.length) return "Flavours at the counter";
  if (x.kind === "scoop") return "Flavours at the counter";
  if (x.kind === "extra") return "Made at the counter";
  const said = names.length ? splitWords(names, x.parts) || names.join(", ") : null;
  return [said, x.size || null].filter(Boolean).join(" · ");
}

/** Each store's day in the summary: its held lines at the hub's prices, or null while any can't be priced. */
export function groupTotals(lines) {
  const out = new Map();
  for (const l of lines) {
    if (l.lapsed) continue;
    const key = `${l.storeId}|${l.date}`;
    const g = out.get(key) || { key, storeId: l.storeId, storeName: l.storeName, date: l.date, cents: 0 };
    g.cents = g.cents === null || typeof l.unitCents !== "number" ? null : g.cents + l.unitCents * l.quantity;
    out.set(key, g);
  }
  return [...out.values()];
}

/** The page's Pick-up/Delivery switched after things are held: the hub keeps an order one way, so say so and offer to
 *  keep it as it is. Null when they agree or nothing's held. */
export function modeClash(pageDelivery, cart) {
  if (!cart.lines.some((l) => !l.lapsed)) return null;
  const held = cart.fulfilment === "delivery";
  if (held === !!pageDelivery) return null;
  return held
    ? { keep: "delivery", text: "Your order’s being delivered, so it can’t switch to pick-up. Take everything out to pick it up, or keep it delivered.", button: "Keep it delivered" }
    : { keep: "pickup", text: "Your order’s a pick-up, so it can’t switch to delivery. Take everything out to have it delivered, or keep it a pick-up.", button: "Keep it a pick-up" };
}

/** A price cell: the hub's figure once it has said it; "Checking…" while it's asked; "Can't check" when it can't say. */
export function priceCell(line, hubState) {
  const known = typeof line.unitCents === "number" && line.priced !== false;
  if (!line.lapsed && !known) return hubState === "idle" || hubState === "loading" ? "Checking…" : "Can’t check";
  if (!line.lapsed && hubState === "cant") return "Can’t check";
  return typeof line.unitCents === "number" ? money(line.unitCents * (line.quantity || 1)) : "";
}

/** Shopify's cart split for the hub's checkout: what goes with the order, and what checks out on its own. */
export function splitShop(shop) {
  const items = (shop && shop.items) || [];
  const refused = items.filter((i) => i.appOnly || i.bar);
  return { merch: items.filter((i) => !i.alone && !i.hub && !i.bar), alone: items.filter((i) => i.alone), ships: items.filter((i) => !i.alone && !i.hub && !i.bar && i.ships), wrong: items.filter((i) => i.hub), refused };
}

/** A Shopify line that can't check out this way, in its own words with the way on (dev/canvas/pages_drops.py): a drop in
 *  its 24 hours in the app (cart.ms-hub's appOnly, the website's time), and a single chocolate bar, which goes in a box.
 *  On a phone Get it in the app opens the app's page; on a laptop the drop's own page, where the code to scan is. */
export function shopRefusal(item, { phone = false, store = null } = {}) {
  if (item.appOnly) return { text: `Only in the app until ${item.appOnly}. Get it in the app, or take it out.`, label: "Get it in the app", href: phone ? item.appHref : item.url, icon: "phone" };
  if (item.bar) return { text: `Bars go in a box. Take it out, or put it in a box at ${store || "a store with chocolate"}.`, label: "Put it in a box of 4", href: `/pages/order?box=4&bar=${encodeURIComponent(item.handle)}#order-chocolate`, icon: "package" };
  return null;
}

/** Under a Shopify line: a drop's store and pick-up day ("Pick-up at Bondi, Wed 23 Dec · Checks out on its own"), with
 *  just "Checks out on its own" when it has no day; posted merch says so. `today` is the store's own day. */
const STATE_PREFIX = /^(NSW|ACT|VIC|QLD|SA|WA|TAS|NT)\s+-\s+/i;
/** The state in a drop's variant name ("NSW - Bondi"), for its store's today; null when it's the store's name alone. */
const dropState = (title) => STATE_PREFIX.exec(String(title || ""))?.[1].toUpperCase() ?? null;
export function dropWords(item, today) {
  if (item.bar) return "Chocolate bar";
  if (!item.alone) return item.ships ? "Posted to you" : "From Shopify";
  if (!item.pickupDate || !item.store) return "Checks out on its own";
  const store = String(item.store).replace(STATE_PREFIX, "").replace(/\s+messina$/i, "").trim();
  const day = dayLabel(item.pickupDate, today);
  return `Pick-up at ${store}, ${day === "Today" || day === "Tomorrow" ? day.toLowerCase() : day} · Checks out on its own`;
}

/** A store's day in the cart: today's pick-up says when it's ready, another day says which day, and a time reads in the
 *  store's own zone. { date, today, storeName, delivery, at, asap, zone } → { head, sub }. */
export function groupWhen({ date, today, storeName, delivery = false, at = null, asap = null, zone = "Australia/Sydney" }) {
  const day = dayLabel(date, today);
  if (delivery) return { head: `Delivered from ${storeName} · ${day}`, sub: "By Uber courier" };
  if (day !== "Today") return { head: at ? `${day} at ${timeLabel(at, zone)}` : day, sub: `Picked up from ${storeName}` };
  return { head: at ? `Ready at ${timeLabel(at, zone)}` : asap ? `Ready ${asap}` : "Ready ASAP", sub: `${storeName}, today` };
}

/** Whether a hold is a delivery: what the line says (Hold it again keeps its order's), else the Order page's switch. */
export function holdDelivery(detail, pageSwitch) {
  return typeof detail.delivery === "boolean" ? detail.delivery : !!pageSwitch;
}

const names = (list) => (list.length > 2 ? `${list.slice(0, 2).join(", ")} and more` : list.join(" and "));

/** Whether checkout can go ahead, what its button says, and the one problem to show. */
export function checkoutState({ cart, hub: hubState, shop, busy = false }) {
  const held = cart.lines.filter((l) => !l.lapsed);
  const lapsed = cart.lines.filter((l) => l.lapsed);
  const split = splitShop(shop);
  let label = "Check out";
  let disabled = busy || (cart.lines.length > 0 && hubState !== "ok");
  let problem = null;
  if (lapsed.length) { label = `Hold your ${kindWord(lapsed)} again to check out`; disabled = true; }
  if (cart.lines.length && hubState === "cant") problem = { text: `We can’t check your ${kindWord(held.length ? held : cart.lines)} ${held.length > 1 ? "are" : "is"} still held.`, retry: true };
  const unpriced = held.filter((l) => l.priced === false);
  if (!problem && unpriced.length) { disabled = true; problem = { text: `We can’t check the price of ${names(unpriced.map((l) => l.title))} right now. Give it a few minutes, then try again.`, retry: true }; }
  // A line that can't go this way says so itself (shopRefusal), and Check out points at it.
  const refused = split.refused.length ? split.refused[0].key : null;
  if (refused) disabled = true;
  if (!problem && split.wrong.length) { disabled = true; problem = { text: `${names(split.wrong.map((i) => i.title))} can’t be bought this way. Take it out, then add it again from the Order page.`, retry: false }; }
  if (!cart.lines.length && !split.merch.length && !split.alone.length) disabled = true;
  // With nothing from a store, Shopify would take a drop through with the rest; a drop checks out on its own.
  if (!problem && !refused && !cart.lines.length && split.alone.length && (split.merch.length || split.alone.length > 1)) {
    const rest = [...split.alone.slice(1), ...split.merch];
    disabled = true;
    problem = { text: `${split.alone[0].title} checks out on its own, so it can’t go through with ${names(rest.map((i) => i.title))}. Take one out to check out, then add it back for its own checkout.`, retry: false };
  }
  if (busy) label = "Checking out";
  const aloneNote = cart.lines.length && split.alone.length
    ? `${names(split.alone.map((i) => i.title))} ${split.alone.length > 1 ? "check" : "checks"} out on its own. Pay for your order first and it’ll stay in your cart for its own checkout.`
    : null;
  return { disabled, label, problem, aloneNote, holdAgain: lapsed, refused };
}

/** The pick-up times chosen for each store and day of a pick-up order: ASAP is no entry; a delivery has none. */
export function pickupsFor(cart, times) {
  if (cart.fulfilment === "delivery") return [];
  const seen = new Set();
  const out = [];
  for (const l of cart.lines) {
    if (l.lapsed) continue;
    const k = `${l.storeId}|${l.date}`;
    if (seen.has(k) || !times[k]) continue;
    seen.add(k);
    out.push({ storeId: l.storeId, date: l.date, at: times[k] });
  }
  return out;
}

/** Where checkout sends them: Shopify's own checkout on this shop, never anywhere else. */
export const invoiceTarget = (url) => safeInvoice(url);

/** The hub's answer folded into the browser's lines: prices, holds that ran out, lines added elsewhere. */
export function mergeAnswer(lines, answer) {
  const byKey = new Map(answer.lines.map((x) => [lineKey({ ...x, flavours: x.flavourIds, parts: x.parts, bars: x.barIds }), x]));
  const out = [];
  for (const l of lines) {
    const k = lineKey(l);
    const x = byKey.get(k);
    if (!x) { if (l.lapsed) out.push(l); continue; }
    byKey.delete(k);
    out.push({ ...l, unitCents: x.unitCents === null ? l.unitCents : x.unitCents, priced: x.unitCents !== null, quantity: x.quantity, storeName: l.storeName || x.store, lapsed: !x.held });
  }
  for (const x of byKey.values()) {
    out.push({ storeId: x.storeId, storeName: x.store, productId: x.productId, kind: x.kind, title: x.product, date: x.date, quantity: x.quantity,
      flavours: x.flavourIds, flavourNames: x.flavours, parts: x.parts, unitCents: x.unitCents, priced: x.unitCents !== null, lapsed: !x.held,
      ...(x.barIds && x.barIds.length ? { bars: x.barIds, barNames: x.bars, title: boxTitle(x.product) } : {}) });
  }
  return out;
}

/** An address form's values checked: { address } when it's whole, or { address: null, errors: { field: words } }. */
export function addressCheck(values, delivery) {
  const v = (n) => String(values[n] || "").trim();
  const a = { firstName: v("firstName"), lastName: v("lastName"), phone: v("phone"), address1: v("address1"), address2: v("address2"), city: v("city"), provinceCode: v("provinceCode") || "NSW", zip: v("zip") };
  const errors = {};
  if (!a.firstName) errors.firstName = "Add your first name.";
  if (!a.lastName) errors.lastName = "Add your last name.";
  if (delivery && !/^\+?[0-9 ]{8,16}$/.test(a.phone)) errors.phone = "Add a mobile number the courier can call.";
  if (!a.address1) errors.address1 = "Add the street address.";
  if (!a.city) errors.city = "Add the suburb.";
  if (!/^\d{4}$/.test(a.zip)) errors.zip = "A postcode is four numbers.";
  if (!a.phone) delete a.phone;
  if (!a.address2) delete a.address2;
  return Object.keys(errors).length ? { address: null, errors } : { address: a, errors };
}

/** The line under Check out: postage still to come, where a courier's address goes in, and how long it's held. The
 *  drawer's is short. */
export function heldLine({ posted = false, delivery = false, until = null, short = false }) {
  const bits = [];
  if (posted) bits.push("Postage added when you pay.");
  if (delivery) bits.push("Your address goes in at checkout, where we check Uber can reach you. Uber’s fee is added when you pay.");
  if (until) bits.push(short ? `Held until ${until}.` : `Held for you until ${until} while you check out.`);
  return bits.join(" ");
}

/** Whose a line or a time is, for its spoken name: "today’s", "tomorrow’s", "Sunday’s". */
export function dayOwn(date, today) {
  const d = dayLabel(date, today);
  if (d === "Today") return "today’s";
  if (d === "Tomorrow") return "tomorrow’s";
  const [y, m, dd] = date.split("-").map(Number), [ty, tm, td] = today.split("-").map(Number);
  const at = new Date(Date.UTC(y, m - 1, dd));
  const ahead = Math.round((at - Date.UTC(ty, tm - 1, td)) / 86400000);
  return ahead > 0 && ahead < 7 ? `${at.toLocaleDateString("en-AU", { weekday: "long", timeZone: "UTC" })}’s` : `the ${d}`;
}

/** A When row on checkout's store card: its label is the day, so another day's pick-up says just its time. */
export function whenRowValue(w, time) {
  if (w.delivery || w.today) return w.when;
  return time || w.when;
}

/** A cart's store days ("storeId|date"), gathered by store in the order they first come, for one card a store. */
export function byStore(keys) {
  const out = [];
  for (const k of keys) {
    const storeId = k.split("|")[0];
    const g = out.find((x) => x.storeId === storeId);
    if (g) g.keys.push(k); else out.push({ storeId, keys: [k] });
  }
  return out;
}

/** What the hub answered: its data, refusals included (they carry their own reason), or null when it couldn't be read. */
export function answerOf(r) { return r.ok ? r.data : r.data && typeof r.data === "object" ? r.data : null; }

/** A checkout the hub refused because of the store's day, its delivery hours or its till: their own words, or null. */
export function checkoutWords(d, store) {
  switch (d.reason) {
    case "pickup-closed": return { text: `${store} has finished pick-ups for today. Tubs are today only, so try another store or come back tomorrow.`, retry: false };
    case "delivery-closed": return String(d.detail || "").startsWith("opens")
      ? { text: `${store} isn’t delivering yet today. Try again a little later.`, retry: false }
      : { text: `${store} has finished delivering for today. Come back tomorrow, or pick it up from another store.`, retry: false };
    case "delivery-not-today": return { text: "Delivery is today only. Take out what’s for another day to have this delivered.", retry: false };
    case "delivery-one-store": return { text: "Delivery comes from one store at a time. Take out what’s from the other store to have these delivered.", retry: false };
    case "store-offline": return { text: `${store}’s till isn’t answering right now, so we can’t send you to pay. Try again in a few minutes.`, retry: true };
    case "needs-address": return { text: "Add the address for your order, then check out again.", retry: false };
    default: return null;
  }
}

/** A hold the hub refused, in the app's words (RefusalWords.swift). */
export function refusalWords(a, store, what) {
  switch (a.reason) {
    case "sold-out": case "toggled-off": case "flavour-sold-out": return `${store} has run out of ${what} for today.`;
    case "not-sold-here": return `${store} doesn’t sell ${what} on the website.`;
    case "channel-off": case "paused": case "closed": return `${store} isn’t taking website orders right now. Pick another store, or try again shortly.`;
    case "today-only": case "past-date": case "tub-not-today": return `${store}’s day has moved on. We’ve read today’s menu again.`;
    case "too-many-held": return "That’s as many as we can hold at once. Take something out to add this.";
    case "store-busy": return `Lots of people are ordering from ${store} right now. Try again in a few minutes.`;
    case "other-fulfilment": return "Your order is coming another way. Check out first, or take out what’s in it.";
    case "pickup-only": return `${what[0].toUpperCase() + what.slice(1)} can only be picked up.`;
    case "delivery-not-today": return "Delivery is today only.";
    case "flavour-unavailable": case "bad-flavours": return `${store} can’t fill a tub with those flavours today. Pick again.`;
    case "bar-sold-out": return `${a.bar || "One of your bars"} is sold out at ${store} now. Swap it for another.`;
    case "bar-unknown": return `We can’t check ${a.bar || "one of your bars"} at ${store} right now. Swap it, or try again in a few minutes.`;
    case "bar-unavailable": return `${store} doesn’t put ${a.bar || "that bar"} in its boxes. Swap it for another.`;
    case "box-not-full": case "box-too-full": case "bad-bars": return "That box takes exactly its number of bars. Pick again.";
    default: return "We couldn’t add it just now. Try again in a moment.";
  }
}

// ── The element (browser only) ────────────────────────────────────────────────────────────────────────────────────

const refusedId = (key) => `refused-${String(key).replace(/[^a-z0-9_-]/gi, "-")}`;
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
function readTimes() { try { return JSON.parse(sessionStorage.getItem(TIMES_KEY) || "{}"); } catch { return {}; } }
function writeTimes(t) { try { sessionStorage.setItem(TIMES_KEY, JSON.stringify(t)); } catch { /* the time stays ASAP */ } }

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  const state = { asap: {}, hub: "idle", expiresAt: null, subtotalCents: null, shop: null, busy: false, problem: null, notes: {}, pendingKey: null, timer: null, toast: null };

  async function readShop() {
    try {
      const r = await fetch("/cart?view=ms-hub", { credentials: "same-origin" });
      const s = r.ok ? JSON.parse(await r.text()) : null;
      state.shop = s && Array.isArray(s.items) && typeof s.totalCents === "number" ? s : null;
    } catch { state.shop = null; }
    return state.shop;
  }

  async function readHub() {
    const cart = loadCart();
    if (!cart.lines.some((l) => !l.lapsed)) { state.hub = "ok"; state.expiresAt = null; state.subtotalCents = 0; return; }
    if (state.hub !== "ok") state.hub = "loading";
    const s = await settle();
    if (s.state === "cant") { state.hub = "cant"; return; }
    state.hub = "ok";
    if (s.state === "paid") { state.expiresAt = null; state.subtotalCents = 0; return; }
    if (s.state !== "open") { lapseAll(); return; }
    const a = s.answer;
    const lines = mergeAnswer(loadCart().lines, a);
    state.expiresAt = a.expiresAt;
    state.subtotalCents = typeof a.subtotalCents === "number" ? a.subtotalCents : null;
    const now = loadCart();
    saveCart(lines.some((l) => !l.lapsed) ? { ...now, expiresAt: a.expiresAt, fulfilment: a.fulfilment || now.fulfilment, lines } : { ...emptyCart(), lines });
  }

  function lapseAll() {
    state.expiresAt = null; state.subtotalCents = 0;
    saveCart({ ...emptyCart(), lines: loadCart().lines.map((l) => ({ ...l, lapsed: true })) });
  }

  async function refresh() {
    state.problem = null;
    if (state.hub === "idle") state.hub = "loading";
    renderAll();
    try {
      await Promise.all([readHub(), readShop(), settleLast().then((s) => {
        // Back from paying: say so, as the board does.
        if (s === "paid") toast(lastPaidStore() ? `Paid. We’ll let you know when ${lastPaidStore()} has it.` : "Paid. We’ll let you know when it’s ready.");
        return cleanPaidMerch();
      }, cleanPaidMerch)]);
    } catch (e) { console.error("Messina cart: couldn't refresh", e); state.hub = "cant"; }
    renderAll();
    watchHold();
  }

  function watchHold() {
    clearTimeout(state.timer);
    if (!state.expiresAt) return;
    const ms = new Date(state.expiresAt).getTime() - Date.now() + 1500;
    if (ms > 0 && ms < 3600000) state.timer = setTimeout(refresh, ms);
  }

  /** Holds a line from the Order page: onto the open checkout, or a new one. */
  async function hold(detail, again = false) {
    const { line, title, store } = detail;
    const cart = loadCart();
    const live = cartLive(cart) && cart.lines.some((l) => !l.lapsed);
    const delivery = holdDelivery(detail, document.querySelector('[data-name="fulfilment"] [aria-checked="true"]')?.textContent.trim() === "Delivery");
    const item = { storeId: line.storeId, productId: line.productId, date: line.date, quantity: line.quantity, ...(line.flavours ? { flavours: line.flavours } : {}), ...(line.parts ? { parts: line.parts } : {}), ...(line.bars ? { bars: line.bars } : {}) };
    if (!storageOk()) { toast("Your browser isn’t keeping anything for this site, so we can’t hold an order here. Try the Messina app."); return; }
    if (!live && !state.pendingKey) state.pendingKey = randomKey();
    const body = live ? { checkoutId: cart.checkoutId, checkoutToken: cart.checkoutToken, items: [item], delivery } : { key: state.pendingKey, items: [item], delivery };
    state.busy = true; renderAll();
    const r = await hub("reserve", body);
    state.busy = false;
    const a = r.ok ? r.data : r.data || { reason: r.reason };
    if (a && a.ok) {
      state.pendingKey = null;
      const c = loadCart();
      const lines = c.lines.filter((l) => lineKey(l) !== lineKey(line));
      lines.push({ ...line, title, storeName: store.name, storeState: store.state || null, kind: detail.kind || null, size: detail.size || null, flavourNames: detail.flavourNames || null, barNames: detail.barNames || null, imageUrl: detail.imageUrl || null, flavourImages: detail.flavourImages || null, priced: undefined });
      saveCart({ checkoutId: a.checkoutId, checkoutToken: a.checkoutToken, expiresAt: a.expiresAt, fulfilment: delivery ? "delivery" : "pickup", lines });
      toast(`Added. We’re holding ${detail.kind === "tub" ? `a ${title}` : title} for you at ${store.name}.`);
      return refresh();
    }
    if (live && !again && ["expired", "closed", "unknown-checkout"].includes(a.reason)) {
      const s = await settle();
      if (s.state !== "paid") lapseAll();
      return hold(detail, true);
    }
    toast(refusalWords(a, store.name, title.toLowerCase()));
    // The Order page can offer the way on: a box whose bar has gone opens again with it taken out.
    document.dispatchEvent(new CustomEvent("ms:hold-refused", { detail: { line, answer: a } }));
    renderAll();
  }

  async function remove(k) {
    const cart = loadCart();
    const l = cart.lines.find((x) => lineKey(x) === k);
    if (!l) return;
    const drop = () => { const c = loadCart(); const lines = c.lines.filter((x) => lineKey(x) !== k); saveCart(lines.length ? { ...c, lines } : emptyCart()); };
    if (l.lapsed || !cart.checkoutId) { drop(); renderAll(); return; }
    state.busy = true; renderAll();
    const r = await hub("remove", { checkoutId: cart.checkoutId, checkoutToken: cart.checkoutToken, item: { storeId: l.storeId, productId: l.productId, date: l.date, ...(l.flavours && l.flavours.length ? { flavours: l.flavours } : {}), ...(l.parts && l.parts.length ? { parts: l.parts } : {}), ...(l.bars && l.bars.length ? { bars: l.bars } : {}) } });
    state.busy = false;
    const a = answerOf(r);
    if (!a) { state.notes[k] = "We couldn’t take it out just now. Try again in a moment."; renderAll(); return; }
    if (a.ok || a.reason === "not-in-cart") { drop(); return refresh(); }
    if (a.reason === "busy") { state.notes[k] = "We’re setting up your payment right now. Try again in a moment."; renderAll(); return; }
    return refresh();
  }

  async function removeShop(key) {
    state.busy = true; renderAll();
    try { await fetch("/cart/change.js", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ id: key, quantity: 0 }) }); } catch { /* read again below */ }
    state.busy = false;
    await readShop(); renderAll();
  }

  async function holdAgain() {
    const cart = loadCart();
    const lapsed = cart.lines.filter((l) => l.lapsed);
    for (const l of lapsed) {
      const c = loadCart();
      saveCart({ ...c, lines: c.lines.filter((x) => lineKey(x) !== lineKey(l)) });
      await hold({ line: { storeId: l.storeId, productId: l.productId, date: l.date, quantity: l.quantity, flavours: l.flavours, parts: l.parts }, title: l.title, store: { id: l.storeId, name: l.storeName, state: l.storeState }, kind: l.kind, size: l.size, flavourNames: l.flavourNames, imageUrl: l.imageUrl, flavourImages: l.flavourImages, delivery: cart.fulfilment === "delivery" });
    }
  }

  const CANT_PAY = { text: "We couldn’t set up your payment just now. Give it a minute.", retry: true };

  async function checkout(el) {
    const cart = loadCart();
    const shop = await readShop();
    const split = splitShop(shop);
    if (!cart.lines.length) { if (split.merch.length || split.alone.length) window.location.assign("/checkout"); return; }
    const st = checkoutState({ cart, hub: state.hub, shop, busy: state.busy });
    if (st.disabled || !shop) { if (!shop) state.problem = { text: "We can’t check your cart right now.", retry: true }; renderAll(); return; }
    const delivery = cart.fulfilment === "delivery";
    const needsAddress = delivery || split.ships.length > 0;
    const form = el.querySelector("[data-cart-address]");
    let address = null;
    if (needsAddress) {
      address = readAddress(form, delivery);
      if (!address) { renderAll(); el.querySelector('[data-cart-address] [aria-invalid="true"]')?.focus(); return; }
    }
    state.busy = true; state.problem = null; renderAll();
    const stop = (p) => { state.busy = false; state.problem = p; renderAll(); };
    let quote = null;
    if (delivery) {
      const q = await hub("quote", { checkoutId: cart.checkoutId, checkoutToken: cart.checkoutToken,
        dropoff: { name: `${address.firstName} ${address.lastName}`, phone: address.phone, address: { street: [address.address1, address.address2].filter(Boolean), city: address.city, state: address.provinceCode, postcode: address.zip, country: "AU" } } });
      const qa = answerOf(q);
      if (!qa) return stop({ text: "We can’t check delivery right now.", retry: true });
      if (!qa.ok) {
        const why = qa.reason;
        if (why === "no-courier") return stop({ text: "Uber can’t bring it to that address right now. Check the address, or try again in a few minutes.", retry: false });
        if (why === "delivery-one-store") return stop({ text: "Delivery comes from one store at a time. Take out what’s from the other store to have these delivered.", retry: false });
        if (["expired", "closed", "unknown-checkout", "pickup-checkout"].includes(why)) { state.busy = false; return refresh(); }
        return stop({ text: "We can’t check delivery right now.", retry: true });
      }
      quote = qa.quote;
    }
    const merch = split.merch.map((i) => ({ variantId: String(i.variantId), quantity: i.quantity, title: i.title }));
    const body = { checkoutId: cart.checkoutId, checkoutToken: cart.checkoutToken, merch: merch.map(({ variantId, quantity }) => ({ variantId, quantity })) };
    if (quote) body.quote = quote;
    if (address) body.customer = { address };
    const pickups = pickupsFor(cart, readTimes());
    if (pickups.length) body.pickups = pickups;
    const d = answerOf(await hub("checkout", body));
    if (!d) return stop(CANT_PAY);
    if (d.ok) {
      const invoice = invoiceTarget(d.invoiceUrl);
      if (!invoice) return stop(CANT_PAY);
      saveCart({ ...loadCart(), sentMerch: merch, expiresAt: d.expiresAt || cart.expiresAt });
      keepInvoice(cart, merch);
      window.location.assign(invoice);
      return;
    }
    if (["expired", "closed", "unknown-checkout", "needs-quote", "other-fulfilment", "changed"].includes(d.reason)) { state.busy = false; return refresh(); }
    if (d.reason === "store-paused") return stop({ text: `${d.store || "The store"} isn’t taking website orders right now, so we can’t send you to pay.`, retry: false });
    if (d.reason === "pickup-time-gone" || d.reason === "bad-pickup") { writeTimes({}); return stop({ text: "That pick-up time has gone. We’ve set it back to ASAP; check out again or pick another time.", retry: false }); }
    if (d.reason === "no-shipping-rate") return stop({ text: "We can’t post your order to that address. Check it, or take out what needs posting.", retry: false });
    if (d.reason === "bad-merch") return stop({ text: "One of your other items can’t go through with your order. Take it out of your cart and try again.", retry: false });
    if (d.reason === "too-many-tries") return stop({ text: "That’s a few tries in a row. Give it a few minutes, then try again.", retry: false });
    const store = (cart.lines.find((l) => l.storeId === d.storeId) || cart.lines[0] || {}).storeName || "The store";
    const words = checkoutWords(d, store);
    if (words) return stop(words);
    return stop(CANT_PAY);
  }

  function readAddress(form, delivery) {
    if (!form) return null;
    const values = Object.fromEntries([...form.querySelectorAll("[name]")].map((i) => [i.name, i.value]));
    const { address, errors } = addressCheck(values, delivery);
    // Kept in state, so the errors survive the panel being drawn again.
    state.addressErrors = errors;
    return address;
  }

  function toast(text) {
    let t = document.querySelector(".toast-live");
    if (!t) { t = document.createElement("div"); t.className = "toast toast-live"; t.setAttribute("role", "status"); document.body.appendChild(t); }
    t.textContent = text;
    t.hidden = false;
    clearTimeout(state.toast);
    state.toast = setTimeout(() => { t.hidden = true; }, 5000);
  }

  function renderAll() { document.querySelectorAll("ms-hub-cart").forEach((el) => el.render()); }

  class MsHubCart extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.compact = this.hasAttribute("compact");
      this.addEventListener("click", (e) => {
        // Check out waits while something can't go through yet (a lapsed hold, a price we can't check).
        const held = e.target.closest('a[aria-disabled="true"], button[aria-disabled="true"][data-checkout]');
        if (held) {
          e.preventDefault();
          const why = held.getAttribute("aria-describedby") && document.getElementById(held.getAttribute("aria-describedby").split(" ")[0]);
          if (why) why.focus();
          return;
        }
        const keepMode = e.target.closest("[data-keep-mode]");
        if (keepMode) { document.querySelectorAll(`[data-name="fulfilment"] [role="radio"]:nth-child(${keepMode.dataset.keepMode === "delivery" ? 2 : 1})`).forEach((r) => r.click()); renderAll(); return; }
        const b = e.target.closest("button");
        if (!b || b.disabled) return;
        if (b.matches("[data-remove]")) remove(b.dataset.remove);
        else if (b.matches("[data-remove-shop]")) removeShop(b.dataset.removeShop);
        else if (b.matches("[data-hold-again]")) holdAgain();
        else if (b.matches("[data-checkout]")) checkout(this);
        else if (b.matches("[data-retry-cart]")) refresh();
      });
      this.render();
    }

    /** "panel" (the Order page's Your order, under its own head), "drawer" (from the header's bag, any page) or "page"
     *  (/cart, the step before Shopify's checkout). */
    get mode() { return this.hasAttribute("compact") ? "panel" : this.hasAttribute("drawer") ? "drawer" : "page"; }

    render() {
      const cart = loadCart();
      const split = splitShop(state.shop);
      const st = checkoutState({ cart, hub: state.hub, shop: state.shop || { items: [] }, busy: state.busy });
      const shopItems = (state.shop && state.shop.items) || [];
      const total = this.total(cart);
      const n = cart.lines.filter((l) => !l.lapsed).length + shopItems.length;
      const keep = this.querySelector("[data-cart-address]");
      const values = keep ? Object.fromEntries([...keep.querySelectorAll("[name]")].map((i) => [i.name, i.value])) : null;
      if (!cart.lines.length && !shopItems.length) this.innerHTML = this.emptyHtml();
      else if (this.mode === "page") this.innerHTML = this.pageHtml(cart, split, st, total);
      else this.innerHTML = this.sideHtml(cart, split, st, total);
      // The drawer's foot is new each render: its sheet measures it again, so no line stays under it.
      if (this.mode === "drawer") this.dispatchEvent(new CustomEvent("ms:sheet-feet", { bubbles: true }));
      if (values) this.querySelectorAll("[data-cart-address] [name]").forEach((i) => { if (values[i.name] !== undefined) i.value = values[i.name]; });
      // The address's errors, from the last Go to payment, until it's sent again.
      const errs = state.addressErrors || {};
      this.querySelectorAll("[data-cart-address] [name]").forEach((i) => {
        const msg = errs[i.name];
        i.toggleAttribute("aria-invalid", !!msg);
        if (msg) i.setAttribute("aria-describedby", `err-${i.name}`); else i.removeAttribute("aria-describedby");
        const err = this.querySelector(`[data-err="${i.name}"]`);
        if (err) { err.id = `err-${i.name}`; err.textContent = msg || ""; err.hidden = !msg; }
      });
      this.bar(n, total);
    }

    emptyHtml() {
      const nothing = `<div class="op-empty">${this.icon("bag")}<p class="t-card">Nothing in it yet</p></div>`;
      if (this.mode === "panel") return `<div class="op-lines">${nothing}</div><div class="op-foot"><p class="t-small t-soft op-held">Add something from the menu and it shows here. It’s held for 15 minutes while you check out.</p></div>`;
      if (this.mode === "drawer") return `<div class="sm-bd">${nothing}</div><div class="sheet-foot pinned"><a class="btn wide" href="/pages/order">${this.icon("bag-sm")}Order from your store</a></div>`;
      return `<div class="panel co-card">${nothing}<div class="btns" style="justify-content: center"><a class="btn" href="/pages/order">Back to the menu</a></div></div>`;
    }

    /** One line: its photo, name, what's in it (a tub's split as a bar of its flavours), Take it out, its price. */
    lineHtml(x) {
      const k = lineKey(x);
      const what = lineWhat(x);
      const pics = x.kind === "tub" && x.flavourImages && x.flavourImages.length > 1 && x.flavours && x.flavours.length
        ? `<span class="split-mini" aria-hidden="true">${x.flavourImages.map((u, i) => `<i style="flex: ${(x.parts && x.parts[i]) || 1}">${u ? `<img src="${esc(u)}" alt="" width="60" height="28" loading="lazy">` : ""}</i>`).join("")}</span>` : "";
      const price = priceCell(x, state.hub);
      return `<div class="o-line${x.lapsed ? " lapsed" : ""}">${x.imageUrl ? `<img class="o-th" src="${esc(x.imageUrl)}" alt="" width="104" height="104" loading="lazy">` : ""}<div style="flex: 1; min-width: 0"><div class="t-card">${esc(x.title)}${x.quantity > 1 ? ` × ${x.quantity}` : ""}</div>`
        + `${what ? `<div class="t-small t-soft">${esc(what)}</div>` : ""}${pics}${x.lapsed ? `<div class="t-small shut-line">No longer held</div>` : ""}${state.notes[k] ? `<div class="t-small" role="status">${esc(state.notes[k])}</div>` : ""}`
        + `<button class="q-link" type="button" data-remove="${esc(k)}" aria-label="Take ${esc(dayOwn(x.date, todayIn(zoneFor(x.storeState))))} ${esc(x.title)} out"${state.busy ? " disabled" : ""}>${this.icon("x")}Take it out</button></div><b class="${/Can|Check/.test(price) ? "t-soft" : ""}">${esc(price)}</b></div>`;
    }

    shopLineHtml(i) {
      const no = shopRefusal(i, { phone: matchMedia("(max-width: 767px)").matches, store: storeChoice()?.name || null });
      const why = no ? `<p class="t-small line-info" id="${esc(refusedId(i.key))}" tabindex="-1" style="margin: 4px 0 0">${this.icon(no.icon === "phone" ? "phone" : "box")}${esc(no.text)}</p>`
        + `<a class="pill" href="${esc(safePath(no.href) || "/")}" style="align-self: flex-start">${this.icon(no.icon === "phone" ? "phone-18" : "box-18")}${esc(no.label)}</a>` : "";
      return `<div class="o-line">${i.image ? `<img class="o-th" src="${esc(i.image)}" alt="" width="104" height="104" loading="lazy">` : ""}<div style="flex: 1; min-width: 0"><div class="t-card">${esc(i.title)}${i.quantity > 1 ? ` × ${i.quantity}` : ""}</div><div class="t-small t-soft">${esc(dropWords(i, todayIn(zoneFor(dropState(i.store)))))}</div>`
        + `${why}<button class="q-link" type="button" data-remove-shop="${esc(i.key)}"${state.busy ? " disabled" : ""}>${this.icon("x")}Take it out<span class="sr">, ${esc(i.title)}</span></button></div><b>${money(i.lineCents)}</b></div>`;
    }

    groups(cart) {
      const out = new Map();
      for (const l of cart.lines) { const k = `${l.storeId}|${l.date}`; if (!out.has(k)) out.set(k, []); out.get(k).push(l); }
      return out;
    }

    when(cart, k, l) {
      const zone = zoneFor(l.storeState);
      const delivery = cart.fulfilment === "delivery";
      const w = groupWhen({ date: l.date, today: todayIn(zone), storeName: l.storeName, delivery, at: readTimes()[k], asap: state.asap[`${l.storeId}|${l.date}`], zone });
      return { ...w, delivery, today: dayLabel(l.date, todayIn(zone)) === "Today", where: `${delivery ? "Delivered from" : "Pick-up from"} ${l.storeName}`, when: delivery ? "ASAP today · by Uber courier" : w.head };
    }

    /** What the Order page and the drawer share: what's held, then Total and Check out, which leads to /cart. */
    sideHtml(cart, split, st, total) {
      const groups = this.groups(cart);
      const here = storeChoice();
      let lines = "";
      for (const [k, ls] of groups) {
        const l = ls[0], w = this.when(cart, k, l);
        const onPage = this.mode === "panel" && groups.size === 1 && here && here.id === l.storeId && w.today;
        if (!onPage) {
          const change = this.mode !== "drawer" || w.delivery || !w.today ? "" : document.getElementById("order-time") ? `<button class="q-link" type="button" data-sheet-open="order-time" aria-haspopup="dialog" aria-label="Change the pick-up time">Change</button>` : `<a class="q-link" href="/pages/order?store=${esc(l.storeId)}" aria-label="Change it on the Order page">Change</a>`;
          lines += `<div class="how-row"><span class="how-ic">${this.icon(w.delivery ? "truck" : w.today ? "pin" : "calendar")}</span><span class="how-tx"><span class="t-small t-soft">${esc(w.where)}</span><span class="t-card">${esc(w.when)}</span></span>${change}</div>`;
        }
        lines += ls.map((x) => this.lineHtml(x)).join("");
      }
      const items = (state.shop && state.shop.items) || [];
      if (items.length) lines += `${cart.lines.length ? `<div class="grp-hd" role="heading" aria-level="3">${split.ships.length === items.length ? "Posted to you" : "From the shop"}</div>` : ""}${items.map((i) => this.shopLineHtml(i)).join("")}`;
      const clash = this.mode === "panel" ? modeClash(document.querySelector('[data-name="fulfilment"] [aria-checked="true"]')?.textContent.trim() === "Delivery", cart) : null;
      const problem = state.problem || st.problem;
      let foot = `<div class="tot t-card"><span>Total so far</span><span>${total ? esc(total) : state.hub === "cant" ? "Can’t check" : "Checking…"}</span></div>`;
      if (st.aloneNote) foot += `<p class="t-small" style="margin: 0">${esc(st.aloneNote)}</p>`;
      if (problem) foot += `<p class="t-small cart-problem" role="status">${esc(problem.text)}${problem.retry ? ` <button class="q-link" type="button" data-retry-cart>Try again</button>` : ""}</p>`;
      if (st.holdAgain.length) foot += `<button class="pill" type="button" data-hold-again${state.busy ? " disabled" : ""}>Hold it again</button>`;
      if (clash) foot += `<p class="t-small line-info" role="status" id="mode-clash">${this.icon("info")}${esc(clash.text)}</p><button class="btn wide" type="button" data-keep-mode="${clash.keep}" aria-describedby="mode-clash">${esc(clash.button)}</button>`;
      else {
        if (document.documentElement.dataset.signedIn !== "1" && cart.lines.length) foot += this.signIn();
        const off = st.disabled || !!st.holdAgain.length;
        foot += `<a class="btn wide" href="${esc(window.Shopify && Shopify.routes ? Shopify.routes.root + "cart" : "/cart")}" data-to-checkout${off ? ' aria-disabled="true"' : ""}${st.refused ? ` aria-describedby="${esc(refusedId(st.refused))}"` : ""}>${this.icon("arrow")}Check out${total && !off ? ` · ${esc(total)}` : ""}</a>`;
      }
      const until = state.expiresAt && cart.lines.some((l) => !l.lapsed) ? timeLabel(state.expiresAt, Intl.DateTimeFormat().resolvedOptions().timeZone) : null;
      const held = heldLine({ posted: !!split.ships.length, delivery: cart.fulfilment === "delivery" && cart.lines.length > 0, until, short: this.mode === "drawer" });
      if (held) foot += `<p class="t-small t-soft op-held">${esc(held)}</p>`;
      if (this.mode === "drawer") foot += `<button class="q-link center" type="button" data-close>Keep ordering</button>`;
      return this.mode === "drawer" ? `<div class="sm-bd">${lines}</div><div class="sheet-foot pinned">${foot}</div>` : `<div class="op-lines">${lines}</div><div class="op-foot">${foot}</div>`;
    }

    /** /cart: everything in one place before Shopify's checkout. Where and when for each store's day, what's in it, the
     *  address when something's posted or delivered, and the summary, which stays in view. */
    pageHtml(cart, split, st, total) {
      const stores = this.stores();
      let main = "", inIt = "";
      const groups = this.groups(cart), items = (state.shop && state.shop.items) || [];
      for (const st of byStore([...groups.keys()])) {
        const days = st.keys.map((k) => { const l = groups.get(k)[0]; return { k, l, w: this.when(cart, k, l) }; });
        const { l, w } = days[0], s = stores[l.storeId] || {};
        const phone = s.phone ? `<div><span class="t-small t-soft">Phone</span><a class="v" href="tel:${esc(String(s.phone).replace(/\s+/g, ""))}">${esc(s.phone)}</a></div>` : "";
        const where = s.address ? `<div><span class="t-small t-soft">${w.delivery ? "From" : "Where"}</span><span class="v">${esc(s.address)}</span></div>` : "";
        const whens = days.map((d) => `<div><span class="t-small t-soft">${d.w.delivery ? "When" : esc(dayLabel(d.l.date, todayIn(zoneFor(d.l.storeState))))}</span><span class="v">${esc(whenRowValue(d.w, readTimes()[d.k] ? timeLabel(readTimes()[d.k], zoneFor(d.l.storeState)) : null))}</span>${d.w.delivery ? "" : `<a class="q-link" href="/pages/order?store=${esc(d.l.storeId)}" aria-label="Change ${esc(dayOwn(d.l.date, todayIn(zoneFor(d.l.storeState))))} pick-up time">Change</a>`}</div>`).join("");
        const dir = s.maps && !w.delivery ? `<div class="btns"><a class="pill" href="${esc(s.maps)}" target="_blank" rel="noopener">${this.icon("pin-sm")}Directions${this.icon("out")}</a></div>` : "";
        main += `<div class="panel co-card"><div class="co-hd">${this.icon(w.delivery ? "truck" : "pin")}<h2 class="t-title">${esc(w.where)}</h2></div><div class="facts co-facts co-rows">${where}${whens}${phone}</div>${dir}</div>`;
        for (const d of days) {
          const day = dayLabel(d.l.date, todayIn(zoneFor(d.l.storeState)));
          const hd = groups.size > 1 || items.length ? `<div class="grp-hd" role="heading" aria-level="3">${esc(d.w.where)}, ${esc(day === "Today" ? "today" : day)}</div>` : "";
          inIt += hd + groups.get(d.k).map((x) => this.lineHtml(x)).join("");
        }
      }
      if (items.length) inIt += `${cart.lines.length ? `<div class="grp-hd" role="heading" aria-level="3">${split.ships.length === items.length ? "Posted to you" : "From the shop"}</div>` : ""}${items.map((i) => this.shopLineHtml(i)).join("")}`;
      main += `<div class="panel co-card"><div class="co-hd">${this.icon("bag-sm")}<h2 class="t-title">What’s in it</h2></div>${inIt}</div>`;
      const delivery = cart.fulfilment === "delivery";
      if ((split.ships.length || delivery) && cart.lines.length) {
        const why = delivery ? "Uber brings it from the store today. We check the courier can reach you before you pay." : "What’s posted goes from Marrickville; the rest stays a pick-up. Your address goes through to Shopify’s checkout with the order, so the postage is there when you pay.";
        main += `<div class="panel co-card"><div class="co-hd">${this.icon(delivery ? "truck" : "package")}<h2 class="t-title">${delivery ? "Where should we deliver it?" : "Where should we post it?"}</h2></div><p class="t-small t-soft" style="margin: 0">${why}</p>${this.addressForm(delivery)}</div>`;
      }
      const problem = state.problem || st.problem;
      if (st.aloneNote) main += `<p class="t-small">${esc(st.aloneNote)}</p>`;
      if (st.holdAgain.length) main += `<div><button class="pill" type="button" data-hold-again${state.busy ? " disabled" : ""}>Hold it again</button></div>`;
      let rows = groupTotals(cart.lines).map((g) => `<div class="co-row"><span>${esc(this.when(cart, g.key, cart.lines.find((x) => `${x.storeId}|${x.date}` === g.key)).where)}</span><span>${g.cents === null ? "Checking…" : money(g.cents)}</span></div>`).join("");
      const shopCents = items.reduce((t, i) => t + i.lineCents, 0);
      if (items.length) rows += `<div class="co-row"><span>${split.ships.length === items.length ? "Posted to you" : "From the shop"}</span><span>${money(shopCents)}</span></div>`;
      if (split.ships.length) rows += `<div class="co-row soft"><span>Postage</span><span>Added when you pay</span></div>`;
      if (delivery) rows += `<div class="co-row soft"><span>Courier</span><span>Added when you pay</span></div>`;
      const off = st.disabled || !!st.holdAgain.length;
      // Held by a line that says why: Check out stays focusable and takes them to that line.
      const hold = st.refused ? ` aria-disabled="true" aria-describedby="${esc(refusedId(st.refused))}"` : off ? " disabled" : "";
      const go = (cls) => `<button class="btn pista wide ${cls}" type="button" data-checkout${hold}${state.busy ? ' aria-busy="true"' : ""}>${this.icon("arrow")}${state.busy ? "Checking out" : "Go to payment"}${total && !off ? ` · ${esc(total)}` : ""}</button>`;
      const held = state.expiresAt && cart.lines.some((l) => !l.lapsed) ? ` Held for you until ${esc(timeLabel(state.expiresAt, Intl.DateTimeFormat().resolvedOptions().timeZone))} while you check out.` : "";
      const sum = `<div class="g-cocoa co-sum-wrap"><div class="stk" style="--r: var(--ms-web-radius-card); --tilt: 0deg; --drop: var(--ms-color-burnt-orange)"><div class="face co-sum"><h2 class="t-title">Summary</h2>${rows}<div class="hair"></div>`
        + `<div class="tot t-card"><span>Total so far</span><span>${total ? esc(total) : state.hub === "cant" ? "Can’t check" : "Checking…"}</span></div>`
        + `${document.documentElement.dataset.signedIn !== "1" && cart.lines.length ? this.signIn() : ""}${problem ? `<p class="t-small cart-problem" role="status">${esc(problem.text)}${problem.retry ? ` <button class="q-link" type="button" data-retry-cart>Try again</button>` : ""}</p>` : ""}`
        + `${go("co-go")}<p class="t-small co-note">Card details go in on Shopify’s checkout.${held}</p></div></div></div>`;
      return `<div class="co-grid"><div class="stack co-main" style="gap: 24px">${main}</div><aside class="co-aside" aria-label="Summary">${sum}</aside></div><div class="orderbar co-bar"><div class="wrap">${go("")}</div></div>`;
    }

    signIn() {
      return `<div class="earn-line"><img src="${esc(this.dataset.badge || "")}" alt="" width="42" height="42"><p class="t-small"><a class="in-link" href="${esc(signInUrl(location.pathname))}">Sign in</a> and this order earns points.</p></div>`;
    }

    /** The stores' addresses and phones, from the page's store records (by hub id). */
    stores() {
      if (!this._stores) { try { this._stores = JSON.parse(document.querySelector("script[data-cart-stores]")?.textContent || "{}"); } catch { this._stores = {}; } }
      return this._stores;
    }

    total(cart) {
      if (state.hub !== "ok" || state.subtotalCents === null || !state.shop) return null;
      const merch = splitShop(state.shop).merch.reduce((s, i) => s + i.lineCents, 0);
      return cart.lines.some((l) => !l.lapsed) || merch ? money((state.subtotalCents || 0) + merch) : null;
    }

    bar(n, total) {
      document.querySelectorAll("[data-order-bar]").forEach((b) => {
        const label = b.querySelector("[data-order-bar-label]");
        if (label) label.textContent = n ? total || `${n} item${n === 1 ? "" : "s"}` : "Empty";
        document.querySelectorAll("[data-drawer-count]").forEach((c) => (c.textContent = n ? `${n} item${n === 1 ? "" : "s"}` : ""));
        const count = b.querySelector("[data-order-bar-count]");
        if (count) { count.hidden = !n; count.textContent = String(n); }
        b.setAttribute("aria-label", n ? `Your order, ${n} item${n === 1 ? "" : "s"}${total ? `, ${total}` : ""}` : "Your order, empty");
        b.classList.toggle("is-empty", !n);
      });
      document.dispatchEvent(new CustomEvent("ms:count", { detail: { count: window.MessinaCart ? window.MessinaCart.count() : n } }));
    }

    addressForm(delivery) {
      const f = (name, label, ac, type = "text", wide = false) => `<label class="field${wide ? " wide" : ""}"><span class="t-small">${label}</span><input class="input" name="${name}" type="${type}" autocomplete="${ac}"><span class="err" data-err="${name}" hidden></span></label>`;
      const states = ["NSW", "ACT", "VIC", "QLD", "SA", "WA", "TAS"].map((s) => `<option>${s}</option>`).join("");
      return `<div class="fields" data-cart-address><div class="t-card">${delivery ? "Where should we deliver it?" : "Where should we post it?"}</div>`
        + f("firstName", "First name", "given-name") + f("lastName", "Last name", "family-name") + (delivery ? f("phone", "Mobile", "tel", "tel", true) : "")
        + f("address1", "Street address", "address-line1", "text", true) + f("address2", "Apartment or unit (if there is one)", "address-line2") + f("city", "Suburb", "address-level2")
        + `<label class="field"><span class="t-small">State</span><select class="input" name="provinceCode" autocomplete="address-level1">${states}</select></label>`
        + f("zip", "Postcode", "postal-code") + `</div>`;
    }

    icon(name) {
      const t = document.getElementById("ms-cart-icons");
      return t?.content.querySelector(`[data-i="${name}"]`)?.innerHTML || "";
    }
  }
  if (!customElements.get("ms-hub-cart")) customElements.define("ms-hub-cart", MsHubCart);

  // Once per page, wherever the cart's script is loaded (the Order page, the cart, a product page): adds from any picker
  // are held here, and the header's bag count can ask.
  if (!window.MessinaCart) {
    window.MessinaCart = { count: () => loadCart().lines.filter((l) => !l.lapsed).reduce((n, l) => n + l.quantity, 0) + ((state.shop && state.shop.items.reduce((n, i) => n + i.quantity, 0)) || 0), refresh };
    document.addEventListener("ms:added", (e) => hold(e.detail));
    // A Shopify add says ms:shop-cart; paid merch cleared after a checkout says ms:shopify-cart. Either rereads it.
    for (const ev of ["ms:shop-cart", "ms:shopify-cart"]) document.addEventListener(ev, () => readShop().then(renderAll));
    document.addEventListener("ms:asap", (e) => { state.asap[`${e.detail.storeId}|${e.detail.date}`] = e.detail.label; renderAll(); });
    // The Order page's Pick-up/Delivery switch: Your order says so when it disagrees with what's held.
    document.addEventListener("ms:mode", () => renderAll());
    // The header's bag says whether its drawer is open.
    for (const [ev, open] of [["ms:sheet-open", true], ["ms:sheet-close", false]]) document.addEventListener(ev, (e) => { if (e.target.id === "cart-drawer") document.querySelectorAll("[data-bag]").forEach((b) => b.setAttribute("aria-expanded", String(open))); });
    document.addEventListener("ms:pickup-time", (e) => { const t = readTimes(); const k = `${e.detail.storeId}|${e.detail.date}`; if (e.detail.at) t[k] = e.detail.at; else delete t[k]; writeTimes(t); renderAll(); });
    addEventListener("pageshow", (e) => { state.busy = false; if (e.persisted) refresh(); });
    refresh();
  }
}
